import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeftLong,
  faArrowUpRightFromSquare,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import './dash.css';
import {
  fetchJobsScoutWithAuth,
  formatDateTime,
  formatShortDate,
  getJobDescription,
} from './jobsScoutShared';

function JobsScoutJobViewComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {jobId = ''} = useParams();
  const [authReady, setAuthReady] = useState(Boolean(auth.currentUser));
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(location.state?.job || null);

  const jobDescription = useMemo(() => getJobDescription(job), [job]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(Boolean(user));
    });

    return () => unsubscribe();
  }, [auth]);

  const loadJob = useCallback(async () => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    if (location.state?.job && location.state.job.id === jobId) {
      setJob(location.state.job);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await fetchJobsScoutWithAuth(
        auth,
        '/jobs?limit=100',
      );
      const jobs = Array.isArray(result.jobs) ? result.jobs : [];
      const matchedJob = jobs.find((item) => item.id === jobId) || null;
      setJob(matchedJob);
    } catch (error) {
      console.error('Failed to load job listing:', error);
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [auth, jobId, location.state]);

  useEffect(() => {
    if (!authReady) return;
    loadJob();
  }, [authReady, loadJob]);

  return (
    <div className='dashComp opportunityScoutPage'>
      <section className='opportunityScoutTopBar'>
        <div className='opportunityScoutTopBarTitle'>
          <h2>View Job Listing</h2>
          <p>Review the saved job listing details, filters used, and captured description.</p>
        </div>
        <div className='opportunityScoutTopBarActions'>
          <Button
            variant='outline-dark'
            className='opportunityScoutSecondaryButton'
            onClick={() => navigate('/jobs-scout')}
          >
            <FontAwesomeIcon icon={faArrowLeftLong} />
            <span>Back to Jobs</span>
          </Button>
        </div>
      </section>

      <section className='opportunityScoutPanel opportunityScoutDetailCard'>
        {loading ? (
          <div className='opportunityScoutLoadingState'>
            <Spinner animation='border' />
          </div>
        ) : !job ? (
          <div className='opportunityScoutEmptyState'>
            <h5>Job not found</h5>
            <p>This listing could not be loaded. It may have been removed or the page was opened without a valid job id.</p>
          </div>
        ) : (
          <>
            <div className='opportunityScoutDetailHeader'>
              <div>
                <p className='opportunityScoutEyebrow'>Job detail</p>
                <h3>{job.jobTitle}</h3>
                <p className='opportunityScoutDetailOrg'>
                  <FontAwesomeIcon icon={faBuilding} /> {job.companyName || 'Unknown company'}
                </p>
              </div>
              {job.sourceUrl && (
                <a
                  href={job.sourceUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='opportunityScoutSourceLink'
                >
                  Open listing <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              )}
            </div>

            <div className='opportunityScoutMetaGrid'>
              <div>
                <span>Location</span>
                <strong>{job.location || 'Not captured'}</strong>
              </div>
              <div>
                <span>Posted via</span>
                <strong>{job.postedVia || 'Google Jobs'}</strong>
              </div>
              <div>
                <span>Posted date</span>
                <strong>{job.postedDateText || formatShortDate(job.postedDateIso)}</strong>
              </div>
              <div>
                <span>Job type</span>
                <strong>{job.jobType || 'Not captured'}</strong>
              </div>
              <div>
                <span>Salary</span>
                <strong>{job.salary || 'Not captured'}</strong>
              </div>
              <div>
                <span>Source domain</span>
                <strong>{job.sourceDomain || 'Not captured'}</strong>
              </div>
              <div>
                <span>Country</span>
                <strong>{job.countryName || 'Not captured'}</strong>
              </div>
              <div>
                <span>Run ID</span>
                <strong>{job.latestRunId || 'Unknown'}</strong>
              </div>
              <div>
                <span>First seen</span>
                <strong>{formatDateTime(job.firstSeenAtMs || job.createdAtMs)}</strong>
              </div>
              <div>
                <span>Last seen</span>
                <strong>{formatDateTime(job.lastSeenAtMs || job.updatedAtMs)}</strong>
              </div>
            </div>

            <div className='opportunityScoutNarrativeGrid'>
              <div className='opportunityScoutNarrativeCard'>
                <h5>Search keyword</h5>
                <p>{job.searchKeyword || 'No search keyword captured.'}</p>
              </div>
              <div className='opportunityScoutNarrativeCard'>
                <h5>Search filters used</h5>
                <p>
                  {[
                    job.searchLocation ? `Location: ${job.searchLocation}` : '',
                    job.searchCompany ? `Company: ${job.searchCompany}` : '',
                    job.searchJobType ? `Type: ${job.searchJobType}` : '',
                    job.searchDatePosted ? `Date posted: ${job.searchDatePosted}` : '',
                  ].filter(Boolean).join(' • ') || 'No extra filters were used.'}
                </p>
              </div>
              <div className='opportunityScoutNarrativeCard'>
                <h5>Record fingerprint</h5>
                <p style={{wordBreak: 'break-all'}}>{job.fingerprint || 'Not captured'}</p>
              </div>
              <div className='opportunityScoutNarrativeCard'>
                <h5>Source URL</h5>
                <p style={{wordBreak: 'break-all'}}>{job.sourceUrl || 'Not captured'}</p>
              </div>
            </div>

            <div className='opportunityScoutPanel' style={{marginTop: '20px', boxShadow: 'none', border: '1px solid #e5e7eb'}}>
              <div className='opportunityScoutPanelHeader'>
                <div>
                  <h4>Job description</h4>
                  <p>The description captured from the scraped job listing.</p>
                </div>
              </div>
              <div
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.95rem',
                  lineHeight: 1.75,
                  background: '#f8fbff',
                  color: '#28435d',
                  padding: '18px',
                  borderRadius: '14px',
                  border: '1px solid rgba(16, 37, 66, 0.08)',
                  minHeight: '220px',
                }}
              >
                {jobDescription || 'No description was captured for this job listing.'}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default JobsScoutJobViewComp;
